import { keepPreviousData, queryOptions, useMutation } from '@tanstack/react-query';
import pino from 'pino';

import { getVirtualCard } from '../components/pages/request/virtualCard';
import { getRemainingValue, getSpentAmount } from '../components/pages/wallet/myCardsListView/cardUtils';
import { notificationStrings, walletStrings } from '../constants/strings';
import type { ApprovalRequest, ContextRequest } from '../repositories/approvalRepository';
import { type VirtualCardDataApiType, type VirtualCardDataType } from '../repositories/paymentRepository';
import { WalletRepository } from '../repositories/walletRepository';
import { queryClient } from '../router/Root';
import { useZustand } from '../zustandStore';

import { companyPropertiesQuery } from './companyQuery';
import { currencyAndTotalQuery, virtualCardQuery } from './paymentQuery';
import { teamQuery, type GetTeamsResponse } from './teamQuery';
import { userQuery } from './userQuery';

export const walletKeys = {
	getUsersListForActiveTeam: 'getUsersListForActiveTeam',
	getWalletDetails: (taskId: string) => ['getWalletDetails', taskId],
	getPaginatedWalletList: ({
		limit = 10,
		page = 0,
		teamId = '',
		owner = '',
		status = 'ACTIVE',
		sortBy = 'CREATION_DATE',
		sortOrder = 'DESC',
	}: Partial<PaginatedWalletListParams> = {}) =>
		['paginatedWalletList', limit, page, teamId, owner, status, sortBy, sortOrder] as const,
	getVirtualCardtask: (virtualCardId: string) => ['getVirtualCardtask', virtualCardId],
};

export type WalletData = {
	id: string;
	handler: string;
	groupId: string;
	state: string;
	createdOn?: string;
	subjectId: string;
	context:
		| {
				request: ContextRequest;
		  }
		| undefined;
	assigner: string;
	assignedOn: string;
	assignees: [];
	request: ApprovalRequest | undefined;
	card: VirtualCardDataType | undefined;
	remainingValue?: number; // added by wallet Loader
	remainingCardValue?: string; // added by wallet Loader
	cardAmount?: string; // added by wallet Loader
	spentAmount?: string; // added by wallet Loader
	spent?: number; // added by wallet Loader
	cardTeam?: GetTeamsResponse | null;
};

type CancelVirtualCardParams = {
	teamId: string;
	userId: string;
	subjectId: string;
	virtualCardId: string | undefined | null;
	isCardOwner: boolean;
};

const logger = pino();

type WalletCardStatus = 'ACTIVE' | 'INACTIVE' | '';
export type WalletSortBy = 'CREATION_DATE' | 'AMOUNT' | '';
export type WalletSortOrder = 'ASC' | 'DESC' | '';

export type PaginatedWalletListParams = {
	limit: number | undefined;
	page: number | undefined;
	teamId: string | undefined;
	owner: string | undefined;
	status: WalletCardStatus | undefined;
	sortBy: WalletSortBy | undefined;
	sortOrder: WalletSortOrder | undefined;
};

export const paginatedMyWalletListQuery = ({
	limit,
	page,
	teamId,
	owner,
	status,
	sortBy,
	sortOrder,
}: Partial<PaginatedWalletListParams>) => {
	const params = { limit, page, teamId, owner, status, sortBy, sortOrder };
	return queryOptions({
		queryKey: walletKeys.getPaginatedWalletList(params),
		queryFn: async () => {
			try {
				const paginatedWalletData = await WalletRepository.getPaginatedWalletList(params);

				if (paginatedWalletData.tasks && paginatedWalletData.tasks.length > 0) {
					const enhancedTasksPromises = paginatedWalletData.tasks.filter(Boolean).map(async task => {
						let enhancedCardData = task;
						let card: VirtualCardDataApiType | null = null;
						let virtualCard: ReturnType<typeof getVirtualCard> | null = null;
						const taskId = task?.request?.requestId || '';

						try {
							const user = await queryClient.ensureQueryData(userQuery);
							const companyPropertiesData = await queryClient.ensureQueryData(
								companyPropertiesQuery(user.companyAccountId, { cardprovider: 'tsgo-cardprovider' }),
							);
							card = await queryClient.ensureQueryData(
								virtualCardQuery({
									documentId: taskId,
									cardProvider: companyPropertiesData?.cardprovider || '',
								}),
							);
							virtualCard = getVirtualCard(card);
							const updatedCard = virtualCard ? Object.assign({}, task.card, virtualCard) : task.card;
							enhancedCardData = {
								...task,
								card: updatedCard,
							};
						} catch (error) {
							logger.error({ error, message: 'Failed to fetch virtual card data' });
							return enhancedCardData;
						}

						const walletInfo = {
							balance: enhancedCardData.card?.balance,
							amount: enhancedCardData?.request?.amount,
							authPaddingPercentage: enhancedCardData?.request?.authPaddingPercentage,
						};

						let remainingValue = 0;
						try {
							remainingValue = getRemainingValue(walletInfo);
						} catch (error) {
							logger.error({ error, message: 'Failed to calculate remaining value' });
							return enhancedCardData;
						}

						let remainingCardValue = '';
						let cardAmount = '';
						let spentAmount = '';
						let spent = 0;

						try {
							remainingCardValue = await queryClient.ensureQueryData(
								currencyAndTotalQuery(enhancedCardData.request?.currency || '', remainingValue),
							);
						} catch (error) {
							logger.error({ error, message: 'Failed to fetch remaining card value' });
							return enhancedCardData;
						}

						try {
							cardAmount = await queryClient.ensureQueryData(
								currencyAndTotalQuery(
									enhancedCardData?.request?.currency || '',
									Number(enhancedCardData?.request?.amount || enhancedCardData.request?.fields?.amount) || 0,
								),
							);
						} catch (error) {
							logger.error({ error, message: 'Failed to fetch card amount' });
							return enhancedCardData;
						}

						try {
							spent = getSpentAmount(walletInfo);
							spentAmount = await queryClient.ensureQueryData(
								currencyAndTotalQuery(enhancedCardData?.card?.currency || '', spent),
							);
						} catch (error) {
							logger.error({ error, message: 'Failed to fetch spent amount' });
							return enhancedCardData;
						}

						return {
							...enhancedCardData,
							remainingCardValue,
							remainingValue,
							cardAmount,
							spentAmount,
							spent,
						};
					});

					const enhancedTasks = await Promise.all(enhancedTasksPromises);

					return {
						count: paginatedWalletData.count,
						tasks: enhancedTasks,
						hasNextPage: paginatedWalletData.hasNextPage,
						// we need this, so we do not disable the next pager when we have null values in tasks
						initialPaginatedWalletDataCount: Number(paginatedWalletData.tasks?.length) || 0,
					};
				}

				return {
					...paginatedWalletData,
					hasNextPage: paginatedWalletData.hasNextPage,
					// we need this, so we do not disable the next pager when we have null values in tasks
					initialPaginatedWalletDataCount: Number(paginatedWalletData.tasks?.length) || 0,
				};
			} catch (error) {
				logger.error({ error });
				throw error;
			}
		},
		placeholderData: keepPreviousData,
	});
};

export const paginatedWalletListQuery = ({
	limit,
	page,
	teamId,
	owner,
	status,
	sortBy,
	sortOrder,
}: Partial<PaginatedWalletListParams>) => {
	const params = { limit, page, teamId, owner, status, sortBy, sortOrder };
	return queryOptions({
		queryKey: walletKeys.getPaginatedWalletList(params),
		queryFn: async () => {
			const paginatedWalletData = await WalletRepository.getPaginatedWalletList(params);

			if (paginatedWalletData.tasks && paginatedWalletData.tasks.length > 0) {
				const enhancedTasksPromises = paginatedWalletData.tasks.filter(Boolean).map(async task => {
					const walletInfo = {
						balance: task.card?.balance,
						amount: task?.request?.amount,
						authPaddingPercentage: task?.request?.authPaddingPercentage,
					};
					const remainingValue = getRemainingValue(walletInfo);

					let remainingCardValue = '';
					let cardAmount = '';
					let spentAmount = '';
					let spent: ReturnType<typeof getSpentAmount> = 0;
					let cardTeam: GetTeamsResponse | null = null;

					try {
						remainingCardValue = await queryClient.ensureQueryData(
							currencyAndTotalQuery(task?.request?.currency || '', remainingValue || 0),
						);
						cardAmount = await queryClient.ensureQueryData(
							currencyAndTotalQuery(
								task?.request?.currency || '',
								Number(task?.request?.amount || task?.request?.fields?.amount) || 0,
							),
						);
						spent = getSpentAmount(walletInfo);
						spentAmount = await queryClient.ensureQueryData(
							currencyAndTotalQuery(task.card?.currency || '', spent || 0),
						);
						if (task?.request?.teamId) {
							cardTeam = await queryClient.ensureQueryData(teamQuery({ teamId: task?.request?.teamId }));
						}
					} catch (error) {
						logger.error({ error });
					}

					return {
						...task,
						remainingCardValue,
						remainingValue,
						cardAmount,
						cardTeam,
						spentAmount,
						spent,
					};
				});

				const enhancedTasks = await Promise.all(enhancedTasksPromises);
				return {
					count: paginatedWalletData.count,
					tasks: enhancedTasks,
					hasNextPage: paginatedWalletData.hasNextPage,
					// we need this, so we do not disable the next pager when we have null values in tasks
					initialPaginatedWalletDataCount: Number(paginatedWalletData.tasks?.length) || 0,
				};
			}

			return {
				...paginatedWalletData,
				hasNextPage: paginatedWalletData.hasNextPage,
				// we need this, so we do not disable the next pager when we have null values in tasks
				initialPaginatedWalletDataCount: Number(paginatedWalletData.tasks?.length) || 0,
			};
		},
		placeholderData: keepPreviousData,
	});
};

export const walletDetailsQuery = (taskId: string) =>
	queryOptions({
		queryKey: walletKeys.getWalletDetails(taskId),
		queryFn: () => WalletRepository.getWalletDetails({ taskId }),
	});

export const virtualCardTask = (virtualCardId: string) =>
	queryOptions({
		queryKey: walletKeys.getVirtualCardtask(virtualCardId),
		queryFn: () => WalletRepository.getVirtualCardTask({ virtualCardId }),
		enabled: Boolean(virtualCardId),
	});

export const useCancelVirtualCardMutationQuery = () => {
	const { setNotification, cards, setCards } = useZustand(state => ({
		setNotification: state.setNotification,
		cards: state.wallet.cards,
		setCards: state.setWalletCards,
	}));
	return useMutation({
		mutationFn: WalletRepository.cancelVirtualCard,
		onSuccess: async (_data, variables: CancelVirtualCardParams) => {
			const updatedCards = cards.filter(item => item.subjectId !== variables.subjectId);
			setCards(updatedCards);
			setNotification([
				{
					type: notificationStrings.SUCCESS,
					message: walletStrings.CANCEL_CARD_SUCCESS,
				},
			]);
		},
		onError: (_data, _variables: CancelVirtualCardParams, error) => {
			logger.error({ error });
			setNotification([
				{
					type: notificationStrings.ERROR,
					message: walletStrings.CANCEL_CARD_ERROR,
				},
			]);
		},
	});
};
